import { memo, useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronDown, Mic } from 'lucide-react'
import { Avatar, IconButton, ToggleGroup, type ToggleGroupItem } from '@/components/play'
import { personToneKey } from '@/lib/profile'
import { nameOrHandle, profileAvatarUrl, stageLabel } from '@/lib/watch/rules'
import type { PublicProfile, StageFace } from '@/lib/watch/types'
import type { EventStage } from '@/lib/watch/useEventStages'
import { useInRoom, useInRoomCounts } from '@/lib/watch/useInRoom'
import { cn } from '@/lib/utils'
import { HostAvatar } from './HostAvatar'
import './watch.css'

export interface StageSwitcherProps {
  /** The event's stages in the switcher's order (`useEventStages`). */
  stages: readonly EventStage[]
  /** The stage on screen. */
  selectedId: string
  onSelect(stage: EventStage): void
  /** The room is connected: only then does it say who is in it (a stage is live while someone on it is). */
  connected: boolean
  /**
   * `bar`: the desktop's row of stages in the event's bar (avatar, name, how many are on stage), as the prototype's
   * header. `list`: the phone's sheet (the chat's head opens it), a row each with its faces.
   */
  variant: 'bar' | 'list'
  /**
   * The bar's All stages, shown while its stages overflow the room it has: opens the list (the Stages sheet). Without it
   * the row only scrolls.
   */
  onMore?(): void
  /** The Stages sheet is open (All stages' `aria-expanded`). */
  moreOpen?: boolean
  className?: string
}

/** A stage's host as shown: a name that draws nothing is the handle; a logo path resolves against the web app. */
const hostOf = (stage: EventStage): PublicProfile => ({
  ...stage.host,
  name: nameOrHandle(stage.host.name, stage.host.handle, 'Host'),
  avatar_url: profileAvatarUrl(stage.host.avatar_url),
})

/**
 * The event's stages, one to watch at a time (docs/features/watch-experience.md, the stage switcher): each is its
 * host's avatar (the live ring while someone on it is in the room), their name, and how many are on it. Play's
 * ToggleGroup, a `pill` row in the bar and a `list` in the sheet: arrow keys move between stages without switching,
 * Enter or Space switches; each stage is named for itself ("Shane’s stage, live, 2 on stage"), the one on screen
 * pressed. Live and the count are LiveKit's word, as the chat's head says them: a seat counts only while its
 * participant is in the connected room.
 *
 * The bar keeps one row: more stages than fit scroll sideways, faded at the edge that has more, with All stages after
 * it (the sheet's list); the stage on screen scrolls into view.
 */
export const StageSwitcher = memo(function StageSwitcher({ stages, selectedId, onSelect, connected, variant, onMore, moreOpen = false, className }: StageSwitcherProps) {
  const list = variant === 'list'
  const counts = useInRoomCounts(stages.map((stage) => stage.members.map((member) => member.identity)))
  const items: ToggleGroupItem<string>[] = stages.map((stage, index) => {
    const host = hostOf(stage)
    const present = connected ? (counts[index] ?? 0) : 0
    return {
      value: stage.id,
      label: stageLabel(host.name, present),
      // The row's name and line say it all; the bar's pill is short, so its tooltip names the stage.
      tooltip: list ? undefined : `${host.name}’s stage`,
      content: list ? (
        <StageRow host={host} present={present} members={stage.members} connected={connected} />
      ) : (
        <StagePill host={host} present={present} />
      ),
    }
  })
  const select = (id: string) => {
    const stage = stages.find((candidate) => candidate.id === id)
    if (stage) onSelect(stage)
  }
  const group = (
    <ToggleGroup
      label="Stages"
      hideLabel
      variant={list ? 'list' : 'pill'}
      value={selectedId}
      onValueChange={select}
      items={items}
      className={cn('watch-stages', list ? 'watch-stages--list' : 'watch-stages--bar', list && className)}
    />
  )
  return list ? group : <StageBar selectedId={selectedId} onMore={onMore} moreOpen={moreOpen} className={className}>{group}</StageBar>
})

/** Which edges of the bar's row have more stages past them. */
type Overflow = { start: boolean; end: boolean }

const NO_OVERFLOW: Overflow = { start: false, end: false }

/**
 * The bar's row in a sideways scroller: the edge with more stages past it fades (the row's own cue), and All stages
 * follows while any do. The stage on screen scrolls into view when it changes (picked from the sheet, say), instantly.
 */
function StageBar({ selectedId, onMore, moreOpen, className, children }: { selectedId: string; onMore?(): void; moreOpen: boolean; className?: string; children: ReactNode }) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [overflow, setOverflow] = useState<Overflow>(NO_OVERFLOW)
  const measure = useCallback(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    // Scrolled from the start (negative in a right-to-left page), and room left past the end.
    const scrolled = Math.abs(scroller.scrollLeft)
    const start = scrolled > 1
    const end = scrolled + scroller.clientWidth < scroller.scrollWidth - 1
    setOverflow((last) => (last.start === start && last.end === end ? last : { start, end }))
  }, [])
  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    measure()
    if (!scroller || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(scroller)
    if (scroller.firstElementChild) observer.observe(scroller.firstElementChild)
    return () => observer.disconnect()
  }, [measure])
  // The stage on screen in view, sideways only (never the page).
  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    const pressed = scroller?.querySelector('[data-pressed]')
    if (!scroller || !pressed) return
    const box = scroller.getBoundingClientRect()
    const item = pressed.getBoundingClientRect()
    if (item.left < box.left) scroller.scrollLeft -= box.left - item.left
    else if (item.right > box.right) scroller.scrollLeft += item.right - box.right
    measure()
  }, [selectedId, measure])
  const more = overflow.start || overflow.end
  return (
    <div className={cn('watch-stages-bar', className)} data-overflow-start={overflow.start || undefined} data-overflow-end={overflow.end || undefined}>
      <div ref={scrollerRef} className="watch-stages-bar__scroller" onScroll={measure}>
        {children}
      </div>
      {more && onMore && (
        <IconButton variant="ghost" label="All stages" aria-haspopup="dialog" aria-expanded={moreOpen} onClick={onMore} className="watch-stages-bar__more">
          <ChevronDown />
        </IconButton>
      )}
    </div>
  )
}

/** A stage in the bar: its host's compact avatar (the live ring while someone on it is in the room), name and count. */
function StagePill({ host, present }: { host: PublicProfile; present: number }) {
  return (
    <>
      <HostAvatar host={host} size="compact" live={present > 0} className="watch-stage__avatar" />
      <bdi className="watch-stage__name">{host.name}</bdi>
      {present > 0 && (
        <span className="watch-stage__count">
          <Mic aria-hidden="true" />
          {present}
        </span>
      )}
    </>
  )
}

/** A stage in the sheet's list: its host's avatar, name and "2 on stage", and the faces in the room. */
function StageRow({ host, present, members, connected }: { host: PublicProfile; present: number; members: readonly StageFace[]; connected: boolean }) {
  return (
    <>
      <HostAvatar host={host} live={present > 0} className="watch-stage__avatar" />
      <span className="watch-stage__main">
        <bdi className="watch-stage__name">{host.name}</bdi>
        <span className="watch-stage__meta">{present > 0 ? `${present} on stage` : 'No one on stage'}</span>
      </span>
      {connected && present > 0 && <StageFacesStack members={members} />}
    </>
  )
}

/** Up to four of the stage's faces that are in the room, overlapping, as the prototype's rows: a picture of who's on. */
function StageFacesStack({ members }: { members: readonly StageFace[] }) {
  return (
    <span className="watch-stage__faces">
      {members.slice(0, 4).map((member) => (
        <PresentFace key={member.identity} member={member} />
      ))}
    </span>
  )
}

function PresentFace({ member }: { member: StageFace }) {
  const present = useInRoom([member.identity])
  if (!present) return null
  return (
    <Avatar
      name={nameOrHandle(member.name, member.handle, '')}
      size="xs"
      tone="auto"
      toneKey={personToneKey({ handle: member.handle, id: member.userId })}
      className="watch-stage__face"
    />
  )
}
